import {
	ErrorFlagger,
	ValidationCallback,
	validate,
	Utils,
	Auth
} from "validation/validate";
import { SearchResults } from "types-and-validators/SearchResults";
import { ValidationErrors } from "validation/ValidationErrors";

type PageErrors<T> = {
	[entryOffset: number]: ValidationErrors<T>;
};

type PageFlagger<T> = ErrorFlagger<SearchResults<T>["page"][0]>;

export function flagValidationErrorsInSearchResults<T>(
	flagValidationErrorsInRow: ValidationCallback<T>,
	input: any,
	utils: Utils<SearchResults<T>>,
	auth: Auth
) {
	const flagger: ValidationCallback<SearchResults<T>> = (
		input,
		{ flag, checkField }
	) => {
		for (const [pageName, isRequired] of [
			["page", true],
			["nextPage", false],
			["prevPage", false]
		] as [keyof SearchResults<T>, boolean][]) {
			checkField(pageName, {
				type: "array",
				isRequired,
				followUpChecks: ({ findInvalid, value }) => {
					validatePage<T>(
						value,
						pageName,
						flagValidationErrorsInRow,
						flag,
						auth
					);
				}
			});
		}
	};

	return flagger(input, utils, auth);
}

function validatePage<T>(
	page: any,
	fieldName: keyof SearchResults<T>,
	flagValidationErrorsInRow: ValidationCallback<T>,
	flag: ErrorFlagger<SearchResults<T>>,
	auth: Auth
) {
	const pageErrors: PageErrors<T> = {};
	for (let entryOffset = 0; entryOffset < page.length; entryOffset++) {
		const pageFlag: PageFlagger<T> = (fieldName, error) => {
			if (pageErrors[entryOffset] === undefined) {
				pageErrors[entryOffset] = {};
			}
			pageErrors[entryOffset][fieldName] = error;
		};
		validate(page[entryOffset], {
			doValidate: flagValidationErrorsInRow,
			withErrors: errors => {
				for (const [fieldName, errorMsg] of Object.entries(errors)) {
					pageFlag(
						fieldName as keyof typeof errors,
						errorMsg && typeof errorMsg === "string"
							? errorMsg
							: "unknown error"
					);
				}
			},
			auth
		});
	}

	for (const [pageOffset, errors] of Object.entries(pageErrors)) {
		for (const [fieldNameInPage, errorMessage] of Object.entries(errors)) {
			flag(
				fieldName,
				`Entry ${pageOffset}: ${fieldNameInPage}: ${errorMessage}`
			);
		}
	}
}
